import { parsePresentationEffect } from '@/_pure/planPresentation';
import type { BooleanOperandRole, PrimitivePlanEffect, PrimitiveVisualTarget } from '../_contract/scenePlan';

function record(value: unknown, fields: readonly string[]): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid ScenePlan presentation object.');
    const result = value as Record<string, unknown>;
    for (const key of Object.keys(result)) {
        if (!fields.includes(key)) throw new Error(`Unknown ScenePlan presentation field '${key}'.`);
    }
    return result;
}

function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('ScenePlan presentation requires nonempty text.');
    return value;
}

export function parsePrimitivePlanTarget(value: unknown): PrimitiveVisualTarget {
    const raw = record(value, ['targetKind', 'itemKey', 'constellationKey', 'wheelRole', 'power', 'variant', 'digit', 'pathKey', 'section']);
    const only = (fields: readonly string[]) => Object.keys(raw).every(key => key === 'targetKind' || fields.includes(key));
    const count = (field: unknown) => typeof field === 'number' && Number.isInteger(field) && field >= 0;
    const operand = (role: unknown): role is BooleanOperandRole => role === 'boolean-input-one' || role === 'boolean-input-two';
    switch (raw.targetKind) {
        case 'item':
            if (Object.keys(raw).length !== 2 || !text(raw.itemKey).startsWith('item:entity:')) throw new Error('Invalid item target.');
            return { targetKind: 'item', itemKey: text(raw.itemKey) };
        case 'constellation':
            if (Object.keys(raw).length !== 2 || !text(raw.constellationKey).startsWith('constellation:entity:')) throw new Error('Invalid constellation target.');
            return { targetKind: 'constellation', constellationKey: text(raw.constellationKey) };
        case 'primitive-rod':
            if (Object.keys(raw).length !== 1) throw new Error('Invalid rod target.');
            return { targetKind: 'primitive-rod' };
        case 'primitive-wheel':
            if (Object.keys(raw).some(key => !['targetKind', 'wheelRole', 'variant'].includes(key))
                || (raw.variant !== undefined && raw.variant !== 'flight' && raw.variant !== 'radix-band' && raw.variant !== 'marker')) {
                throw new Error('Invalid wheel target.');
            }
            return { targetKind: 'primitive-wheel', wheelRole: text(raw.wheelRole),
                ...(raw.variant === undefined ? {} : { variant: raw.variant }) };
        case 'natural-band': case 'natural-counted-cells': {
            if (Object.keys(raw).some(key => !['targetKind', 'wheelRole', 'power', 'variant'].includes(key))
                || typeof raw.power !== 'number' || !Number.isInteger(raw.power) || raw.power < 0
                || (raw.variant !== undefined && raw.variant !== 'flight' && raw.variant !== 'radix-band' && raw.variant !== 'marker')) {
                throw new Error('Invalid band target.');
            }
            return { targetKind: raw.targetKind, wheelRole: text(raw.wheelRole), power: raw.power,
                ...(raw.variant === undefined ? {} : { variant: raw.variant }) };
        }
        case 'boolean-disc':
            if (Object.keys(raw).some(key => !['targetKind', 'wheelRole', 'variant'].includes(key))
                || (raw.variant !== undefined && raw.variant !== 'flight')) throw new Error('Invalid disc target.');
            return { targetKind: 'boolean-disc', wheelRole: text(raw.wheelRole),
                ...(raw.variant === undefined ? {} : { variant: raw.variant }) };
        case 'natural-digit':
            if (!only(['wheelRole', 'power', 'digit', 'variant']) || !count(raw.power) || !count(raw.digit)
                || (raw.variant !== undefined && raw.variant !== 'flight' && raw.variant !== 'radix-band'
                    && raw.variant !== 'marker' && raw.variant !== 'ante')) throw new Error('Invalid digit target.');
            return { targetKind: 'natural-digit', wheelRole: text(raw.wheelRole), power: raw.power as number, digit: raw.digit as number,
                ...(raw.variant === undefined ? {} : { variant: raw.variant }) };
        case 'boolean-channel':
            if (!only(['pathKey', 'section'])
                || (raw.section !== 'output' && raw.section !== 'output-near' && raw.section !== 'output-far')) throw new Error('Invalid channel target.');
            return { targetKind: 'boolean-channel', pathKey: text(raw.pathKey), section: raw.section };
        case 'boolean-operand': case 'boolean-traveller':
            if (!only(['pathKey', 'wheelRole']) || !operand(raw.wheelRole)) throw new Error(`Invalid ${raw.targetKind} target.`);
            return { targetKind: raw.targetKind, pathKey: text(raw.pathKey), wheelRole: raw.wheelRole };
        default: throw new Error(`Unknown ScenePlan visual target '${raw.targetKind}'.`);
    }
}

export function parsePrimitivePlanEffects(values: readonly unknown[]): readonly PrimitivePlanEffect[] {
    let orbits = 0, sweeps = 0;
    return values.map(raw => {
        const effect = record(raw, ['effectKind', 'target', 'caption', 'marks', 'durationMs', 'angleRadians']);
        const target = parsePrimitivePlanTarget(effect.target);
        if (effect.effectKind === 'pointer-sweep') {
            if (++sweeps > 1 || target.targetKind !== 'primitive-rod'
                || typeof effect.durationMs !== 'number' || !Number.isFinite(effect.durationMs)
                || effect.durationMs <= 0 || effect.durationMs > 60000
                || effect.caption !== undefined || effect.marks !== undefined || effect.angleRadians !== undefined) {
                throw new Error('Invalid or conflicting pointer sweep.');
            }
            return { effectKind: 'pointer-sweep', target, durationMs: effect.durationMs };
        }
        if (effect.effectKind === 'orbit' && ++orbits > 1) throw new Error('Invalid or conflicting orbit effect.');
        return parsePresentationEffect(effect, () => target);
    });
}
